import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { FadeUp, MaskLines } from "../components/Reveal";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [all, setAll] = useState([]);

  useEffect(() => {
    setProject(null);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data)).catch(() => setProject(false));
    api.get("/projects").then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  if (project === null) {
    return (
      <main className="pt-40 pb-32 px-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">Loading…</p>
      </main>
    );
  }

  if (project === false) {
    return (
      <main className="pt-40 pb-32 px-6">
        <h1 className="font-serif text-4xl">Project not found.</h1>
        <Link to="/projects" data-testid="project-detail-back-button" className="link-underline mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.22em]">
          ← All Projects
        </Link>
      </main>
    );
  }

  const idx = all.findIndex((p) => p.id === project.id);
  const prev = idx >= 0 && all.length > 1 ? all[(idx - 1 + all.length) % all.length] : null;
  const next = idx >= 0 && all.length > 1 ? all[(idx + 1) % all.length] : null;

  const meta = [
    ["Location", project.location],
    ["Area", project.area],
    ["Year", project.year],
    ["Typology", project.typology],
  ].filter(([, v]) => v);

  return (
    <main className="pt-28 sm:pt-36">
      <div className="px-6 sm:px-10 max-w-[1400px] mx-auto">
        <Link
          to="/projects"
          data-testid="project-detail-back-button"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#57534E] hover:text-[#181816] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> All Projects
        </Link>

        <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <MaskLines
            as="h1"
            delay={0.1}
            className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
            lines={[project.title]}
          />
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D] md:pb-3 shrink-0">
            {project.category}
          </p>
        </div>

        <FadeUp>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-y hairline py-8" data-testid="project-detail-meta">
            {meta.map(([k, v]) => (
              <div key={k}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">{k}</p>
                <p className="mt-2 font-serif text-lg sm:text-xl">{v}</p>
              </div>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="mt-12 frame-clip img-zoom bg-[#EFECE6]">
            <img src={project.cover_image} alt={project.title} className="w-full aspect-[16/10] object-cover" />
          </div>
        </FadeUp>

        <div className="mt-16 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">The Story</p>
          </div>
          <div className="md:col-span-6">
            <p className="font-light text-[#57534E] leading-relaxed text-lg">{project.description}</p>
            {project.story && (
              <p className="mt-6 font-light text-[#57534E] leading-relaxed">{project.story}</p>
            )}
          </div>
          <div className="md:col-span-3 md:col-start-10">
            {project.materials?.length > 0 && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">Material Palette</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {project.materials.map((m) => (
                    <span key={m} className="border border-[#181816]/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#57534E]">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {project.gallery?.length > 0 && (
          <div className="mt-16 grid md:grid-cols-2 gap-8">
            {project.gallery.map((g, i) => (
              <FadeUp key={i} className={i % 2 === 1 ? "md:mt-12" : ""}>
                <div className="frame-clip img-zoom bg-[#EFECE6]">
                  <img src={g} alt={`${project.title} — view ${i + 1}`} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                </div>
              </FadeUp>
            ))}
          </div>
        )}
      </div>

      {(next || prev) && (
        <div className="mt-24 border-t hairline px-6 sm:px-10 py-16">
          <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-8">
            {prev && (
              <Link to={`/projects/${prev.slug}`} data-testid="project-detail-prev" className="group">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">← Previous</p>
                <h3 className="mt-3 font-serif text-2xl sm:text-4xl group-hover:italic transition-all">{prev.title}</h3>
              </Link>
            )}
            {next && (
              <Link to={`/projects/${next.slug}`} data-testid="project-detail-next" className="group md:text-right">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">Next →</p>
                <h3 className="mt-3 font-serif text-2xl sm:text-4xl group-hover:italic transition-all inline-flex items-center gap-3">
                  {next.title} <ArrowRight className="h-5 w-5" />
                </h3>
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="px-6 sm:px-10 pb-24">
        <div className="max-w-[1400px] mx-auto bg-[#EFECE6] px-8 sm:px-14 py-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <h3 className="font-serif text-2xl sm:text-4xl">Commission a space like this.</h3>
          <Link
            to="/contact"
            data-testid="project-detail-inquire"
            className="inline-flex items-center gap-3 bg-[#181816] text-[#F8F6F0] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#B85D3B] transition-colors duration-300"
          >
            Inquire Studio <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </main>
  );
}
